
import { DBFile, FileType } from '@/features/file-overview/types';
import { useQuery } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { toCamelCase, toSnakeCase } from './case';
import { baseUrl } from './config';
import { CommitInfo, JobQueueStats, LLMModel, MultipartUploadPart, SystemStatus } from './types';

export type PaginatedResponse<t> = {
    total: number;
    results: t[];
};

export type Id = { id: string | number };


// T360 uploads are hidden from Asset 360 from the start.
export type UploadPipeline = 'transition_360';

export async function initiateMultipartUpload(
    client: AxiosInstance,
    props: {
        filename: string;
        fileSize: number;
        contentType: string;
        fileType: FileType;
        checksum?: string;
        pipeline?: UploadPipeline;
    },
    signal?: AbortSignal,
): Promise<DBFile> {
    const request = {
        content_type: props.contentType,
        file_name: props.filename,
        file_size: props.fileSize,
        file_type: props.fileType,
        checksum: props.checksum,
        pipeline: props.pipeline,
    };
    const response = await client.post(`/api/files/initiate_upload/`, request, { signal });
    return toCamelCase(response.data);
}

interface CheckDuplicateFileMatch {
    id: number;
    name: string;
}

export interface CheckDuplicateResponse {
    matched: boolean;
    file: CheckDuplicateFileMatch | null;
}

// Advisory-only pre-upload check (EOL-1950 AC5): never blocks or rejects the
// upload, whether or not a match is found.
export async function checkDuplicateFile(
    client: AxiosInstance,
    props: { projectId: number; checksum: string },
    signal?: AbortSignal,
): Promise<CheckDuplicateResponse> {
    const response = await client.get(`/api/files/check_duplicate`, {
        params: { project_id: props.projectId, checksum: props.checksum },
        signal,
    });
    return toCamelCase(response.data);
}

export async function generatePartUrl(
    client: AxiosInstance,
    props: {
        objectKey: string;
        partNumber: number;
    },
    signal?: AbortSignal,
): Promise<string> {
    const request = {
        objectKey: props.objectKey,
        partNumber: props.partNumber,
    };
    const response = await client.post<{ url: string }>(
        `/api/files/generate_part_url/`,
        toSnakeCase(request),
        { signal },
    );
    return response.data.url;
}

export async function completeMultipartUpload(
    client: AxiosInstance,
    props: {
        objectKey: string;
        parts: MultipartUploadPart[];
    },
    signal?: AbortSignal,
) {
    const request = {
        object_key: props.objectKey,
        parts: props.parts,
    };
    await client.post(`/api/files/complete_upload/`, request, { signal });
}

export const useLLMModels = () => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['llmModels'],
        queryFn: async () => {
            const res = await client.get(`/api/search/llm_models`);
            return toCamelCase(res.data) as LLMModel[];
        },
        enabled,
    });
};

export const useCommitInfo = () => {
    const { client, enabled } = useAuthAxios();
    return useQuery({
        queryKey: ['commit-info'],
        queryFn: async () => {
            let res = await client.get(`/api/commit-info/`);
            return toCamelCase<CommitInfo>(res.data);
        },
        enabled,
    });
};

export const useHealthCheck = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['healthCheck'],
        queryFn: async () => {
            let res = await client.get(`/api/healthcheck`);
            return toCamelCase<SystemStatus>(res.data);
        },
        enabled,
    });
};

export const useFlag = (
    flag: string,
    isEnabled: boolean | undefined = true,
) => {
    const { client, enabled } = useAuthAxios();

    return useQuery<boolean, Error>({
        queryKey: ['feature-flags', flag],
        queryFn: async () => {
            let res = await client.get(`/api/flags/${flag}`);
            return res.data;
        },
        refetchInterval: 10000,
        staleTime: 5000,
        retry: 1,
        enabled: enabled && isEnabled,
    });
};

export interface FeatureFlag {
    name: string;
    enabled: boolean;
}

export const useFlags = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery<FeatureFlag[], Error>({
        queryKey: ['feature-flags-all'],
        queryFn: async () => {
            const res = await client.get('/api/flags');
            return res.data;
        },
        enabled,
    });
};

export const useJobQueue = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery({
        queryKey: ['job-queue'],
        queryFn: async () => {
            const res = await client.get(`${baseUrl}/api/admin/job-queue`);
            return toCamelCase<JobQueueStats>(res.data);
        },
        enabled,
        refetchInterval: 2000,
    });
};

export const useProducts = () => {
    const { client, enabled } = useAuthAxios();

    return useQuery<string[], Error>({
        queryKey: ['products'],
        queryFn: async () => {
            const res = await client('/api/products');
            return res.data as string[];
        },
        enabled,
        // We want to persist between reloads but still refetch in the background
        meta: {
            persist: true,
        },
    });
};

export const useProduct = (product: string) => {
    const { data: products, isLoading, error } = useProducts();

    return {
        data: products?.includes(product) ?? false,
        isLoading,
        error,
    };
};
